import { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Chip,
  Divider,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import SurveyViewerModal from "./SurveyViewerModal";
import useLocalization from "../../hooks/useLocalization";

const CallViewModal = ({ call, open, onClose }) => {
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const language = useSelector((state) => state.authentication.language);
  const isFrench = String(language || "")
    .toLowerCase()
    .startsWith("fr");
  const { getLabel } = useLocalization();

  const getLocalizedName = (entity, fallbackName, fallbackFrenchName) => {
    const defaultName = fallbackName || "";
    if (!entity || typeof entity !== "object") {
      return isFrench
        ? fallbackFrenchName || defaultName
        : defaultName || fallbackFrenchName || "";
    }

    if (isFrench) {
      return (
        entity.name_fr || fallbackFrenchName || entity.name || defaultName || ""
      );
    }

    return (
      entity.name || defaultName || entity.name_fr || fallbackFrenchName || ""
    );
  };

  if (!call) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "complete":
        return "#4caf50";
      case "incomplete":
        return "#f44336";
      default:
        return "#9e9e9e";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: "100%", maxWidth: "800px" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Call Docket</Typography>
        <IconButton
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/* Call Status */}
          <Box>
            <Chip
              label={call.callStatus || "unknown"}
              sx={{
                backgroundColor: getStatusColor(call.callStatus),
                color: "white",
                textTransform: "capitalize",
                fontWeight: 600,
                fontSize: "1rem",
                padding: "20px 16px",
              }}
            />
          </Box>

          <Divider />

          {/* Project Information */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Project Information
            </Typography>
            <Stack
              spacing={1.5}
              sx={{ mt: 1 }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Project Name
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500 }}
                >
                  {getLocalizedName(
                    call.project,
                    call.projectName,
                    call.projectName_fr,
                  ) || getLabel("unknown")}
                </Typography>
              </Box>
              {call.client?.name && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Client
                  </Typography>
                  <Typography variant="body1">
                    {getLocalizedName(
                      call.client,
                      call.client?.name,
                      call.client?.name_fr,
                    )}
                  </Typography>
                </Box>
              )}
              {(call.project?.startDate || call.project?.endDate) && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Project Dates
                  </Typography>
                  <Typography variant="body1">
                    {call.project?.startDate &&
                      format(new Date(call.project.startDate), "MMM d, yyyy")}
                    {call.project?.startDate && call.project?.endDate && " - "}
                    {call.project?.endDate &&
                      format(new Date(call.project.endDate), "MMM d, yyyy")}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Location Information */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Location Information
            </Typography>
            <Stack
              spacing={1.5}
              sx={{ mt: 1 }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Location Name
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500 }}
                >
                  {getLocalizedName(
                    call.location,
                    call.locationName,
                    call.locationName_fr,
                  ) || getLabel("unknown")}
                </Typography>
              </Box>
              {call.location?.locationNumber && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Location Number
                  </Typography>
                  <Typography variant="body1">
                    {call.location.locationNumber}
                  </Typography>
                </Box>
              )}
              {call.location?.address && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {call.location.address.streetAddress}
                    {call.location.address.streetAddress && <br />}
                    {call.location.address.city},{" "}
                    {call.location.address.province}{" "}
                    {call.location.address.postal}
                  </Typography>
                </Box>
              )}
              {call.banner?.name && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Banner
                  </Typography>
                  <Typography variant="body1">
                    {getLocalizedName(
                      call.banner,
                      call.banner?.name,
                      call.banner?.name_fr,
                    )}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Call Details */}
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Call Details
            </Typography>
            <Stack
              spacing={1.5}
              sx={{ mt: 1 }}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Scheduled Date
                </Typography>
                <Typography variant="body1">
                  {call.scheduleDate
                    ? format(new Date(call.scheduleDate), "EEEE, MMMM d, yyyy")
                    : call.yyyymmdd || "Unknown"}
                </Typography>
              </Box>
              {call.callDuration && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Duration
                  </Typography>
                  <Typography variant="body1">
                    {call.callDuration} minutes
                  </Typography>
                </Box>
              )}
              {call.homeMileage !== null &&
                call.homeMileage !== undefined &&
                call.homeMileage !== "" && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Home Mileage
                    </Typography>
                    <Typography variant="body1">{call.homeMileage}</Typography>
                  </Box>
                )}
              {call.startedAt && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Started At
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(call.startedAt), "h:mm a")}
                  </Typography>
                </Box>
              )}
              {call.completedAt && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Completed At
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(call.completedAt), "h:mm a")}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Call Type
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ textTransform: "capitalize" }}
                >
                  {call.callType || "regular"}
                </Typography>
              </Box>
              {call.isLocked && (
                <Box>
                  <Chip
                    label="Locked"
                    color="warning"
                    size="small"
                  />
                </Box>
              )}
              {call.firstCall && (
                <Box>
                  <Chip
                    label="First Call"
                    color="info"
                    size="small"
                  />
                </Box>
              )}
            </Stack>
          </Box>

          {/* Rep Notes */}
          {call.repNotes && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  Rep Notes
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                >
                  {call.repNotes}
                </Typography>
              </Box>
            </>
          )}

          {/* Surveys */}
          {call.surveys && call.surveys.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  Surveys ({call.surveys.length})
                </Typography>
                <Stack
                  spacing={1}
                  sx={{ mt: 1 }}
                >
                  {call.surveys.map((survey, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        padding: 1.5,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        backgroundColor: "#fafafa",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500 }}
                        >
                          {getLocalizedName(
                            survey,
                            survey.surveyName || survey.name,
                            survey.surveyName_fr || survey.name_fr,
                          ) || `Survey ${idx + 1}`}
                        </Typography>
                        {survey.status && (
                          <Chip
                            label={survey.status}
                            size="small"
                            sx={{ mt: 0.5, textTransform: "capitalize" }}
                          />
                        )}
                      </Box>
                      {survey && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setSelectedSurvey(survey)}
                        >
                          View Survey
                        </Button>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      {/* Survey Viewer Modal */}
      <SurveyViewerModal
        survey={selectedSurvey}
        open={!!selectedSurvey}
        onClose={() => setSelectedSurvey(null)}
      />
    </Dialog>
  );
};

export default CallViewModal;
